/**
 * Centralized API endpoint paths.
 * Prefer these helpers in service calls (and migrate hardcoded paths over time).
 */

// Auth
export const AUTH = {
  login: '/api/auth/login',
  signup: '/api/auth/signup',
  logout: '/api/auth/logout',
}

// User
export const USER = {
  current: '/api/user',
  companies: '/api/user/companies',
}

// Impersonation
export const IMPERSONATE = {
  start: (userId: string) => `/api/impersonate/${userId}`,
  stop: '/api/impersonate/stop',
}

// Companies
export const COMPANY = {
  overview: (id: string) => `/api/company/${id}`,
  role: (id: string) => `/api/company/${id}/role`,
  profile: (id: string) => `/api/company/${id}/profile`,
  settings: (id: string) => `/api/company/${id}/settings`,
  pipelines: (id: string) => `/api/company/${id}/pipelines`,
  sync: (id: string) => `/api/company/${id}/sync`,
  syncStatus: (id: string) => `/api/company/${id}/sync/status`,
  timezoneOptions: (id: string) => `/api/company/${id}/timezone-options`,
  stateOptions: (id: string) => `/api/company/${id}/state-options`,

  // Keywords
  keywords: (id: string) => `/api/company/${id}/keywords`,
  toggleKeyword: (id: string, ckId: string) => `/api/company/${id}/keywords/${ckId}`,

  // Leads
  contacts: (id: string) => `/api/company/${id}/contacts`,
  calls: (id: string) => `/api/company/${id}/calls`,
  callRecording: (id: string, callId: string) => `/api/company/${id}/calls/${callId}/recording`,
  callAnalytics: (id: string) => `/api/company/${id}/call-analytics`,
  salesSummary: (id: string) => `/api/company/${id}/sales-summary`,
  salesSummaryRecommendations: (id: string) => `/api/company/${id}/sales-summary/recommendations`,
  salesSummaryRecommendationStatus: (id: string, recId: string) =>
    `/api/company/${id}/sales-summary/recommendations/${recId}/status`,
  salesSummaryHistory: (id: string) => `/api/company/${id}/sales-summary/history`,
  leadAnalytics: (id: string) => `/api/company/${id}/lead-analytics`,

  // Opportunities
  opportunities: (id: string) => `/api/company/${id}/opportunities`,

  // Costs
  costs: (id: string) => `/api/company/${id}/costs`,
  cost: (id: string, costId: string) => `/api/company/${id}/costs/${costId}`,

  // Users & Invitations
  users: (id: string) => `/api/company/${id}/users`,
  usersSearch: (id: string) => `/api/company/${id}/users/search`,
  userAccess: (id: string, userId: string) => `/api/company/${id}/users/${userId}`,
  inviteUser: (id: string) => `/api/company/${id}/users/invite`,
  invitations: (id: string) => `/api/company/${id}/users/invitations`,
  expiredInvitations: (id: string) => `/api/company/${id}/users/invitations/expired`,
  resendInvitation: (id: string, invId: string) => `/api/company/${id}/invitations/${invId}/resend`,
  reinvite: (id: string, invId: string) => `/api/company/${id}/invitations/${invId}/reinvite`,
  deleteInvitation: (id: string, invId: string) => `/api/company/${id}/invitations/${invId}`,
}

// Reports
export const REPORTS = {
  list: '/api/reports',
  create: '/api/reports',
  get: (id: string) => `/api/reports/${id}`,
  update: (id: string) => `/api/reports/${id}`,
  delete: (id: string) => `/api/reports/${id}`,
  shares: (id: string) => `/api/reports/${id}/shares`,
  share: (id: string, shareId: string) => `/api/reports/${id}/shares/${shareId}`,
  sendShare: (id: string, shareId: string) => `/api/reports/${id}/shares/${shareId}/send`,
}

// Keyword Metrics
export const KEYWORD_METRICS = {
  profileRanking: (companyId: string) => `/api/keyword-metrics/${companyId}/profile-ranking`,
  geo: (companyId: string) => `/api/keyword-metrics/${companyId}/geo`,
  competitorGeo: (companyId: string) => `/api/keyword-metrics/${companyId}/competitor-geo`,
}

// Admin (system admin)
export const ADMIN = {
  users: '/api/admin/users',
  usersAll: '/api/admin/users/all',
  agencies: '/api/admin/agencies',
  companies: '/api/admin/companies',
  agencyMetrics: '/api/admin/agency-metrics',
  agencyUsers: '/api/admin/agency-users',
  agencyCompanyAccess: '/api/admin/agency-company-access',
  importCompanies: '/api/admin/import-companies',
  importCompaniesManual: '/api/admin/import-companies-manual',
  availableCompanies: (agencyId: string) => `/api/admin/agencies/${agencyId}/available-companies`,
  archiveCompany: (id: string) => `/api/admin/companies/${id}/archive`,
  restoreCompany: (id: string) => `/api/admin/companies/${id}/restore`,
  archivedCompanies: '/api/admin/companies/archived',
  deactivateUser: (userId: string) => `/api/admin/users/${userId}/deactivate`,
  reactivateUser: (userId: string) => `/api/admin/users/${userId}/reactivate`,
  llmPrompts: '/api/admin/llm-prompts',
  llmPrompt: (id: string) => `/api/admin/llm-prompts/${id}`,
}

// Agency
export const AGENCY = {
  users: '/api/agency/users',
  companies: '/api/agency/companies',
  keywords: '/api/agency/keywords',
  companyKeywords: '/api/agency/company-keywords',
  industries: '/api/agency/industries',
  metrics: '/api/agency/metrics',
}

// GHL
export const GHL = {
  locations: '/api/ghl/locations',
  location: (companyId: string) => `/api/ghl/locations/${companyId}`,
}

// Invitations (public)
export const INVITATIONS = {
  get: (token: string) => `/api/invitations/${token}`,
  accept: (token: string) => `/api/invitations/${token}/accept`,
}

// Shared Reports (public)
export const SHARED = {
  report: (token: string) => `/api/shared/report/${token}`,
}
